/**
 * SitePick front — dataLayer push ラッパ（docs/10 §6）
 *
 * `window.dataLayer` への push はここだけ。GTM 未導入でも配列に積むだけで無害。
 * Phase 4 の対象イベント: view_item_list / select_item / add_to_wishlist /
 * remove_from_wishlist / add_to_compare / remove_from_compare / view_comparison。
 */

/** 本番として扱うホスト（これ以外は検証環境）。 */
const PRODUCTION_HOSTS = ["sitepick.jp", "www.sitepick.jp"];

/**
 * 実行環境を判定する（production / staging / local）。
 * ホスト名の**完全一致**で見る（部分一致だと staging も本番と誤認するため）。
 *
 * @returns {string}
 */
function environment() {
  try {
    const host = window.location.hostname.toLowerCase();
    if (PRODUCTION_HOSTS.indexOf(host) !== -1) {
      return "production";
    }
    return host === "staging.sitepick.jp" ? "staging" : "local";
  } catch {
    return "local";
  }
}

/** GA4 ecommerce の入れ子（`ecommerce` オブジェクト）へ移すキー。 */
const ECOMMERCE_KEYS = ["items", "value", "currency", "transaction_id", "item_list_id", "item_list_name"];

/**
 * visitor classification（external / internal / test / unknown）。
 * `inc/gtm.php` が plugin の判定（ログイン中の運営 / 署名付き Cookie）を `window.SITEPICK_VISITOR_TYPE` に出す。
 * 未定義（plugin 無効・古いテーマ）なら "unknown"（external と断定しない）。
 *
 * @returns {string}
 */
export function visitorType() {
  const v = window.SITEPICK_VISITOR_TYPE;
  return v === "external" || v === "internal" || v === "test" ? v : "unknown";
}

/**
 * Growth 分析用の流入印（ChatGPT Ads の oppref 窓なら "openai_ads"。無ければ ""）。`inc/gtm.php` が出す。
 * Google 広告は GA4 の session 帰属に任せる（印を付けない）。
 *
 * @returns {string}
 */
export function trafficSource() {
  return window.SITEPICK_TRAFFIC_SOURCE === "openai_ads" ? "openai_ads" : "";
}

/**
 * @param {string} event
 * @param {Object} payload
 * @returns {void}
 */
export function push(event, payload = {}) {
  try {
    const env = environment();
    const entry = Object.assign({ event }, payload, { sp_env: env, sp_visitor_type: visitorType() });
    const source = trafficSource();
    if (source) {
      entry.sp_traffic_source = source;
    }
    // 検証環境（local / staging / GTM プレビュー）のイベントを本番の数字に
    // 混ぜないための印。GA4 の内部トラフィック フィルタが traffic_type を見る。
    if (env !== "production") {
      entry.traffic_type = "internal";
    }
    // ブラウザ QA が立てる印（qa/lib/browser.mjs）。実ユーザーには付かない。
    if (window.SITEPICK_QA === true) {
      entry.sp_qa = true;
      entry.traffic_type = "internal";
    }
    // 運営の閲覧（internal / test）は sp_env を変えず、traffic_type も付けない:
    // GA4 のデータフィルタで永久に消さず raw として残し、Growth 集計が sp_visitor_type で分ける（docs/17 §V）。
    window.dataLayer = window.dataLayer || [];
    // GA4 ecommerce（items / value / currency …）は GTM の `DLV - ecommerce` が読む `ecommerce` の下に置く。
    // 前のイベントの ecommerce が残らないよう、先に null で消す（Google 推奨）。
    if (Array.isArray(entry.items)) {
      const ecommerce = {};
      for (const key of ECOMMERCE_KEYS) {
        if (entry[key] !== undefined) {
          ecommerce[key] = entry[key];
        }
      }
      window.dataLayer.push({ ecommerce: null });
      entry.ecommerce = ecommerce;
    }
    window.dataLayer.push(entry);
  } catch {
    /* analytics の失敗で UI を止めない */
  }
}

/**
 * Consent Mode v2 の更新スタブ（docs/10 §6 / docs/11 §14）。
 *
 * 既定値（`granted` / EU は `denied`）は GTM スニペットより前に `inc/gtm.php` が出す。
 * 将来 CMP（同意バナー）を入れたら、利用者の選択後にこれを呼んで更新する。
 * v1 では呼び出し元は無い（日本 BtoB 前提・既定許可）。
 *
 * @param {Object} state 例 { analytics_storage: "granted", ad_storage: "denied" }
 * @returns {void}
 */
export function updateConsent(state = {}) {
  try {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", state);
      return;
    }
    // gtag 未定義（GTM 未設定）でも壊れないように gtag と同じ arguments 形状で積む。
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 0: "consent", 1: "update", 2: state });
  } catch {
    /* 失敗しても UI を止めない */
  }
}

/**
 * カード / 詳細に埋めた `data-sp-item='{json}'` を読む（docs/10 §6.4）。
 *
 * @param {Element|null} el
 * @returns {Object|null}
 */
export function readItem(el) {
  if (!el) {
    return null;
  }
  const holder = el.closest("[data-sp-item]");
  if (!holder) {
    return null;
  }
  try {
    return JSON.parse(holder.getAttribute("data-sp-item"));
  } catch {
    return null;
  }
}

/**
 * batch API のレスポンス 1 件を items スキーマへ寄せる。
 *
 * @param {Object} site
 * @param {number} [index]
 * @returns {Object}
 */
export function siteToItem(site, index) {
  const item = {
    item_id: site.sku || String(site.id || ""),
    item_name: site.name || "",
    item_brand: "SITE PICK",
    currency: site.currency || "JPY",
  };
  // セール中は割引後（サーバーが計算した effective_price）。JS では割引を計算しない。
  const price = site.has_discount && typeof site.effective_price === "number" ? site.effective_price : site.price;
  if (typeof price === "number") {
    item.price = price;
  }
  if (Array.isArray(site.industry) && site.industry[0]) {
    item.item_category = site.industry[0].name || "";
  }
  if (Array.isArray(site.purpose) && site.purpose[0]) {
    item.item_category2 = site.purpose[0].name || "";
  }
  if (Array.isArray(site.impression) && site.impression[0]) {
    item.item_category3 = site.impression[0].name || "";
  }
  if (Number.isInteger(index)) {
    item.index = index;
  }
  return item;
}

export function addToWishlist(item) {
  if (item) {
    push("add_to_wishlist", { currency: item.currency || "JPY", value: item.price || 0, items: [item] });
  }
}

export function removeFromWishlist(item) {
  if (item) {
    push("remove_from_wishlist", { items: [item] });
  }
}

export function addToCompare(id, count) {
  push("add_to_compare", { item_id: id, compare_count: count });
}

export function removeFromCompare(id, count) {
  push("remove_from_compare", { item_id: id, compare_count: count });
}

export function viewItemList(listId, listName, items) {
  push("view_item_list", { item_list_id: listId, item_list_name: listName, items });
}

export function viewComparison(items, selectedImpression) {
  push("view_comparison", { items, selected_impression: selectedImpression || "" });
}

/**
 * Stripe から商品詳細へ戻ってきた（`?checkout=cancelled`。measurement.yaml `checkout_return`）。
 * 1 回の戻りにつき 1 回。URL の印は呼び出し側（checkout.js）が消す。
 *
 * @param {string} itemId
 * @param {string} returnType 現状は "cancel" のみ（許可リスト）
 */
export function checkoutReturn(itemId, returnType) {
  push("checkout_return", { item_id: itemId || "", return_type: returnType || "cancel" });
}

/**
 * 開いたままの Stripe の手続きを再開した（`POST /checkout` が resume で返った。measurement.yaml `checkout_resume`）。
 * `begin_checkout` は最初の受理のときだけなので、再挑戦はこちらで数える。
 *
 * @param {string} itemId
 * @param {string} checkoutRef サーバが返す結合キー（予約 ID の HMAC 先頭 16 桁。無ければ ""）
 */
export function checkoutResume(itemId, checkoutRef) {
  push("checkout_resume", { item_id: itemId || "", checkout_ref: checkoutRef || "" });
}

/**
 * Preview 閲覧 / デバイス切替（docs/10 §6.3 の `view_preview`）。
 *
 * **デモ（iframe）の実ロードが成功したときだけ**呼ぶ。読み込み開始や失敗では呼ばない
 * （growth/ssot/measurement.yaml の semantics_fix）。デバイス切替時は「表示中の
 * 再エンゲージ」として呼ぶ。
 *
 * `entry` は「どこから実物に入ったか」（docs/17 §W.6）。業種 LP の FV から直行できるように
 * したことで view_preview は構造的に増えるため、増えた分と従来分を混ぜないために分ける。
 * これが無いと「preview が増えた」を改善と誤読する。
 *
 * @param {string} itemId
 * @param {string} device "pc" | "sp"
 * @param {string} [entry] "industry_fv" | "card" | "detail"
 */
export function viewPreview(itemId, device, entry) {
  push("view_preview", { item_id: itemId || "", device: device || "pc", preview_entry: entry || "detail" });
}

/**
 * デモの実ロードに失敗した（timeout）ことの観測（measurement.yaml `preview_error`）。
 * `view_preview`（成功）と対で「開いたのに見られなかった」率を出す。
 *
 * @param {string} itemId
 * @param {string} reason 現状は "timeout" のみ
 */
export function previewError(itemId, reason) {
  push("preview_error", { item_id: itemId || "", reason: reason || "timeout" });
}

/**
 * カラーを切り替えた（measurement.yaml `preview_color`・docs/07 §13）。
 * 色の案を用意した商品でだけ発火する。color = 案の key（標準は "base"）。
 * color_location = card（一覧カード）/ detail（商品詳細のデバイス枠）/ live（実物確認画面）。
 *
 * @param {string} itemId
 * @param {string} color
 * @param {string} [location]
 */
export function previewColor(itemId, color, location) {
  const where = ["card", "detail", "live"].includes(location) ? location : "live";
  push("preview_color", { item_id: itemId || "", color: color || "base", color_location: where });
}

/**
 * 「自社の情報で見る」の操作（docs/22 §9 `customize_demo`）。実物確認画面だけで発火する。
 * 送るのは列挙値だけ（入力した文字・ファイル名・画像・px・S/M/L・内部の ref は送らない）。
 * `open` はページ表示あたり 1 回、それ以外はデモの反映の確認（applied）を受けたときに 1 回（呼び出し側が守る）。
 * `view_preview`（広告の入札用 CV）には触れない。
 */
export const CUSTOMIZE_ACTIONS = [
  "open", "logo", "logo_width", "company", "image", "text", "text_size", "reveal", "reset", "export", "reload_top",
  "landing_shown", "landing_open", "landing_close",
];

/** `customize_section` に使ってよい TOP のパーツの語（data-section の語彙）。これ以外は other。 */
export const CUSTOMIZE_SECTIONS = [
  "hero", "about", "concept", "message", "services", "service", "strengths", "features", "reasons", "works", "cases",
  "voice", "voices", "gallery", "products", "menu", "people", "staff", "team", "faq", "flow", "price", "plan",
  "news", "company", "access", "hours", "numbers", "profile", "policy", "recruit", "first-visit", "cta", "contact",
];

/** `customize_error` の reason。 */
export const CUSTOMIZE_ERROR_REASONS = [
  "no_manifest", "no_ack", "untrusted_frame", "image_too_large", "image_type", "image_decode", "limit", "unsupported", "error",
];

/**
 * @param {string} itemId SKU
 * @param {string} action CUSTOMIZE_ACTIONS のどれか（それ以外は送らない）
 * @param {{section?: string, device?: string}} [opts]
 * @returns {void}
 */
export function customizeDemo(itemId, action, opts = {}) {
  if (CUSTOMIZE_ACTIONS.indexOf(action) === -1) {
    return;
  }
  const payload = {
    item_id: String(itemId || "").slice(0, 64),
    customize_action: action,
    device: opts.device === "sp" ? "sp" : "pc",
  };
  if (typeof opts.section === "string" && opts.section !== "") {
    payload.customize_section = CUSTOMIZE_SECTIONS.indexOf(opts.section) !== -1 ? opts.section : "other";
  }
  push("customize_demo", payload);
}

/**
 * 「自社の情報で見る」の失敗（docs/22 §9 `customize_error`）。reason は列挙値だけ（未知は error）。
 *
 * @param {string} itemId
 * @param {string} reason
 * @returns {void}
 */
export function customizeError(itemId, reason) {
  push("customize_error", {
    item_id: String(itemId || "").slice(0, 64),
    reason: CUSTOMIZE_ERROR_REASONS.indexOf(reason) !== -1 ? reason : "error",
  });
}

/**
 * 一覧の絞り込み結果（measurement.yaml `filter_sites`）。
 *
 * 結果が差し替わったときに 1 回だけ呼ぶ。**`result_count === 0` がゼロ件シグナル**で、
 * 商品企画の需要検知に使う（ゼロ件専用イベントは作らない = 二重計上を避ける）。
 *
 * @param {Object} params 絞り込み条件（sp_* ）と result_count
 */
export function filterSites(params) {
  const p = Object.assign({}, params);
  // ゼロ件の回数を GA4 で数えるための印（result_count はカスタム指標で「0 の回数」が取れないため。
  // 別イベントは作らない = 二重計上しない。measurement.yaml filter_sites.zero_result）。
  if (typeof p.result_count === "number") {
    p.zero_result = p.result_count === 0 ? "1" : "0";
  }
  push("filter_sites", p);
}

/**
 * `checkout_error` の reason の許可リスト（measurement.yaml `checkout_error`）。
 * GA4 の dimension を任意の文字列で汚さないため、ここに無い値は送らない（"error" に丸める）。
 */
export const CHECKOUT_ERROR_REASONS = ["forbidden", "rate_limited", "not_found", "coupon", "unavailable", "retry", "error", "network"];

/**
 * `POST /checkout` の失敗応答を reason に写す（純関数。checkout.js の announce() の分岐順と同じ）。
 *
 * | HTTP / 条件                     | reason        |
 * |---------------------------------|---------------|
 * | 429                             | rate_limited  |
 * | 503 / `retryable`               | retry         |
 * | 409 `coupon_no_longer_valid`    | coupon        |
 * | 409 `partner_self_unavailable`  | coupon        |
 * | 409 その他                      | unavailable   |
 * | 403                             | forbidden     |
 * | 404                             | not_found     |
 * | その他（5xx 等）                | error         |
 * | fetch の例外（status = 0）       | network       |
 *
 * @param {number} status HTTP ステータス（fetch が例外なら 0）
 * @param {Object} [data] 応答 JSON（code / retryable を読む）
 * @returns {string}
 */
export function checkoutErrorReason(status, data) {
  const body = data && typeof data === "object" ? data : {};
  if (!status) {
    return "network";
  }
  if (status === 429) {
    return "rate_limited";
  }
  if (status === 503 || body.retryable) {
    return "retry";
  }
  if (status === 409) {
    // パートナー本人の購入（SELF）を開始できなかった 409 も価格の文脈の問題（商品は買える）なので coupon に寄せる
    // （許可リストに新しい値を足さない。docs/21 §3.2）。
    return body.code === "coupon_no_longer_valid" || body.code === "partner_self_unavailable" ? "coupon" : "unavailable";
  }
  if (status === 403) {
    return "forbidden";
  }
  if (status === 404) {
    return "not_found";
  }
  return "error";
}

/**
 * 購入ボタンを押したが Checkout を開始できなかった（measurement.yaml `checkout_error`）。
 * `begin_checkout`（サーバ受理）と対で「押したのに始まらなかった」数を出す。
 *
 * @param {string} itemId SKU
 * @param {string} reason {@link CHECKOUT_ERROR_REASONS} のいずれか
 */
export function checkoutError(itemId, reason) {
  push("checkout_error", {
    item_id: itemId || "",
    reason: CHECKOUT_ERROR_REASONS.includes(reason) ? reason : "error",
  });
}

/**
 * 共有（GA4 推奨イベント `share`。site-share spec §14.1）。
 *
 * 送るのは「遷移」（line / email = 外部アプリへ渡した）と「完了」（web_share の resolve / copy の成功）だけ。
 * キャンセル・失敗・パネルを開いただけでは送らない。値は固定語彙と SKU だけ（URL・タイトル・PII は送らない）。
 *
 * @param {{ method: string, content_type: string, item_id: string, share_location: string }} params
 *   method = web_share | copy | line | email / content_type = product | demo /
 *   share_location = detail_fv | detail_foot | live_foot | live_float
 */
export function share(params) {
  const p = params || {};
  push("share", {
    method: String(p.method || ""),
    content_type: String(p.content_type || ""),
    item_id: String(p.item_id || ""),
    share_location: String(p.share_location || ""),
  });
}

/** `ask_open` の許可リスト（custom-chat spec §14。diagnosis = かんたん診断の結果ページ。docs/18 §5）。 */
const ASK_ENTRIES = ["detail_top", "detail_foot", "sticky", "live", "diagnosis"];
const ASK_MODES = ["chat", "mail", "contact"];
/** 相談種別（販売導線 v2 Phase 1。general = この商品について質問・確認 / semiorder = このサイトのイメージでオリジナル制作を相談）。 */
const ASK_TYPES = ["general", "semiorder"];

/**
 * 公開ページの「相談する」クリック（analysis event。Key Event にしない・generate_lead に相乗りしない）。
 * 本文・メールアドレスは送らない（会話画面は private route で GTM を読まない）。
 *
 * @param {string} itemId SKU（無ければ post ID）
 * @param {string} entry  detail_top / detail_foot / sticky / live / diagnosis
 * @param {string} mode   chat / mail / contact
 * @param {string} [type] general / semiorder（無ければ general）
 * @returns {void}
 */
export function askOpen(itemId, entry, mode, type) {
  push("ask_open", {
    item_id: String(itemId || "").slice(0, 64),
    ask_entry: ASK_ENTRIES.indexOf(entry) !== -1 ? entry : "other",
    ask_mode: ASK_MODES.indexOf(mode) !== -1 ? mode : "contact",
    ask_type: ASK_TYPES.indexOf(type) !== -1 ? type : "general",
  });
}

/** かんたん診断（販売導線 v2 Phase 2・docs/18 §5）の許可リスト（measurement.yaml view_diagnosis 他）。 */
const DIAGNOSIS_ENTRIES = ["top", "sites", "sites_empty", "industry", "guide", "footer", "direct"];
const DIAGNOSIS_STEPS = ["1", "2", "3", "4", "5", "6"];
const DIAGNOSIS_SCALES = ["small", "mid", "large", "any"];

/**
 * 入口の値を allowlist に丸める（未知は direct）。
 *
 * @param {string} entry
 * @returns {string}
 */
function diagnosisEntry(entry) {
  return DIAGNOSIS_ENTRIES.indexOf(entry) !== -1 ? entry : "direct";
}

/**
 * 回答の slug（term slug / allowlist の値）を GA4 に出せる形に丸める。配列は "," 連結（filter_sites の sp_* と同じ）。
 * 英数字・`_`・`-` 以外は落とす（自由入力は無いが、DOM 改変で任意文字列を送らせない）。
 *
 * @param {string|string[]} value
 * @returns {string}
 */
function diagnosisSlug(value) {
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((v) => String(v || "").toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 64))
    .filter(Boolean)
    .slice(0, 4)
    .join(",");
}

/**
 * /diagnosis/ の表示（1 回/pageview）。sp_industry はプリフィル時だけ。
 *
 * @param {string} entry top / sites / sites_empty / industry / guide / footer / direct
 * @param {string} [industry] プリフィルされた業種 slug
 * @returns {void}
 */
export function viewDiagnosis(entry, industry) {
  const p = { diagnosis_entry: diagnosisEntry(entry) };
  const ind = diagnosisSlug(industry || "");
  if (ind !== "") {
    p.sp_industry = ind;
  }
  push("view_diagnosis", p);
}

/**
 * 最初の回答操作（1 回）。
 *
 * @param {string} entry
 * @returns {void}
 */
export function diagnosisStart(entry) {
  push("diagnosis_start", { diagnosis_entry: diagnosisEntry(entry) });
}

/**
 * 設問の確定（設問ごとに 1 回）。回答内容は送らない。
 *
 * @param {string} step "1" .. "6"（業態 "1b" は "6" に寄せる）
 * @param {string} entry
 * @returns {void}
 */
export function diagnosisStep(step, entry) {
  const s = step === "1b" ? "6" : String(step || "");
  push("diagnosis_step", {
    diagnosis_step: DIAGNOSIS_STEPS.indexOf(s) !== -1 ? s : "1",
    diagnosis_entry: diagnosisEntry(entry),
  });
}

/**
 * 送信の直前（1 回。リロード再送は diagnosis.js が抑止）。回答は slug / allowlist の値だけ。
 *
 * @param {string} entry
 * @param {{industry?: string[], purpose?: string[], impression?: string[], feature?: string[], scale?: string[]}} answers
 * @returns {void}
 */
export function diagnosisComplete(entry, answers) {
  const a = answers || {};
  const scale = diagnosisSlug(a.scale || "");
  const p = {
    diagnosis_entry: diagnosisEntry(entry),
    sp_industry: diagnosisSlug(a.industry || ""),
    sp_purpose: diagnosisSlug(a.purpose || ""),
    sp_impression: diagnosisSlug(a.impression || ""),
    sp_feature: diagnosisSlug(a.feature || ""),
    diagnosis_scale: DIAGNOSIS_SCALES.indexOf(scale) !== -1 ? scale : "any",
  };
  Object.keys(p).forEach((k) => {
    if (p[k] === "") {
      delete p[k];
    }
  });
  push("diagnosis_complete", p);
}

/**
 * /diagnosis/result/ の描画（1 回/pageview）。result_count / zero_result は filter_sites と同じ意味。
 *
 * @param {{result_count: number, diagnosis_entry: string, item_id: string}} params
 * @returns {void}
 */
export function viewDiagnosisResult(params) {
  const p = params || {};
  const count = Number.isFinite(p.result_count) ? Math.max(0, Math.trunc(p.result_count)) : 0;
  const payload = {
    result_count: count,
    zero_result: count === 0 ? "1" : "0",
    diagnosis_entry: diagnosisEntry(p.diagnosis_entry),
  };
  const itemId = String(p.item_id || "").slice(0, 64);
  if (itemId !== "") {
    payload.item_id = itemId;
  }
  push("view_diagnosis_result", payload);
}

/** 請求書払いの申請（販売導線 v2 Phase 3・docs/18 §5）の入口の許可リスト（measurement.yaml view_invoice_form / request_invoice）。 */
const INVOICE_ENTRIES = ["detail_top", "detail_foot", "hold"];

/**
 * 入口の値を allowlist に丸める（未知は detail_top）。
 *
 * @param {string} entry
 * @returns {string}
 */
function invoiceEntry(entry) {
  return INVOICE_ENTRIES.indexOf(entry) !== -1 ? entry : "detail_top";
}

/**
 * /purchase/invoice/ の表示（1 回/pageview）。PII は送らない。
 *
 * @param {string} itemId SKU
 * @param {string} entry  detail_top / detail_foot / hold
 * @returns {void}
 */
export function viewInvoiceForm(itemId, entry) {
  push("view_invoice_form", {
    item_id: String(itemId || "").slice(0, 64),
    invoice_entry: invoiceEntry(entry),
  });
}

/**
 * 申請の受付（POST 成功の直後の受付完了でサーバが ack を描いたときだけ 1 回）。Key Event にしない・Ads へ送らない。
 * value は申請時の価格（契約・売上ではない）。ecommerce の下には置かない（purchase と混ざらないように）。
 *
 * @param {{item_id: string, invoice_entry: string, value: number, currency: string}} params
 * @returns {void}
 */
export function requestInvoice(params) {
  const p = params || {};
  const value = Number.isFinite(p.value) ? Math.max(0, Math.trunc(p.value)) : 0;
  push("request_invoice", {
    item_id: String(p.item_id || "").slice(0, 64),
    invoice_entry: invoiceEntry(p.invoice_entry),
    value,
    currency: "JPY",
  });
}
